import { isNumber, mapValues, round } from "lodash";
import React from "react";
import { OPEN_UNLIMITED, Report, ReportCollection } from "types";
import { ProductRenderer } from "../ColumnTypes";
import { CustomProductFilter } from "../CustomFilters/CustomProductFilter";
import { CustomReport } from "../CustomReport";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";

export const CapacityReport = ({ report }: { report: Report }) => {
    const columns = React.useMemo(
        () =>
            getColumnDefs({
                report,
            }),
        [report],
    );

    const formattedReport = React.useMemo(() => {
        if (!report.collection) {
            return { ...report, collection: ReportCollection.CAPACITY };
        }

        return report;
    }, [report]);

    return <CustomReport dateFields={["start"]} report={formattedReport} columns={columns} unwind={unwind} />;
};

const unwind = (data: any[]) => {
    const unwoundData: any = [];
    if (data.length > 0) {
        mapValues(data[0], (availabilityForDate: any, experienceId: string) => {
            mapValues(availabilityForDate, (timeslots: any, date: string) => {
                mapValues(timeslots, (info: any, time: string) => {
                    const capacity = info.capacity < OPEN_UNLIMITED ? info.capacity : "Unlimited";
                    const open = info.open < OPEN_UNLIMITED ? info.open : "Unlimited";
                    const isCapacityLimited = isNumber(capacity) && isNumber(open);
                    const hasAvailableCapacity = capacity !== 0 && open !== 0;
                    const isCapacityExhausted = capacity !== 0 && open === 0;

                    const isUtilizationApplicable = isCapacityLimited && (hasAvailableCapacity || isCapacityExhausted);
                    const utilization = isUtilizationApplicable ? round(100 * (1 - info.open / info.capacity)) : "N/A";

                    const contributingFactors = [];
                    if (isNumber(info.max)) {
                        contributingFactors.push("Capacity manually adjusted");
                    }

                    if (info.resourceRestricted) {
                        contributingFactors.push("Equipment shortage");
                    }

                    const quantity = info.event.quantity
                        ? info.event.quantity
                        : {
                              confirmed: 0,
                              pending: 0,
                              waitlist: 0,
                              noshow: 0,
                              canceled: 0,
                          };

                    unwoundData.push({
                        ...info,
                        capacity,
                        open,
                        utilization,
                        contributingFactors: contributingFactors.join(","),
                        confirmed: quantity.confirmed,
                        pending: quantity.pending,
                        waitlist: quantity.waitlist,
                        canceled: quantity.canceled,
                        noshow: quantity.noshow,
                        product: { id: experienceId },
                        start: date,
                        arrivalDate: date,
                        arrivalTime: time,
                    });
                });
            });
        });
    }

    return unwoundData;
};
type GetColumnDefsParams = {
    report: Report;
};
const getColumnDefs = ({ report }: GetColumnDefsParams): ReportColumnDef[] => [
    {
        field: "product",
        headerName: "Product",
        type: "product",
        filter: CustomProductFilter,
        filterParams: { report },
        maxWidth: 250,
        cellRenderer: ProductRenderer,
    },
    {
        field: "start",
        headerName: "Arrival Date",
        type: "date",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "arrivalTime",
        headerName: "Arrival Time",
        type: "arrivalTime",
    },
    {
        field: "open",
        headerName: "Open",
        filter: false,
    },
    {
        field: "capacity",
        headerName: "Capacity",
        filter: false,
    },
    {
        field: "confirmed",
        headerName: "Confirmed",
        filter: false,
        type: "number",
    },
    {
        field: "pending",
        headerName: "Pending",
        filter: false,
        type: "number",
    },
    {
        field: "waitlist",
        headerName: "Waitlist",
        filter: false,
        type: "number",
    },
    {
        field: "noshow",
        headerName: "No Show",
        filter: false,
        type: "number",
    },

    {
        field: "canceled",
        headerName: "Canceled",
        filter: false,
        type: "number",
    },
    {
        field: "utilization",
        headerName: "Utilization %",
        filter: "agNumberColumnFilter",
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "contributingFactors",
        filter: false,
        headerName: "Contributing Factors",
    },
];
